import {
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import { NeedStatus, useGetProcurementPlanQuery, useListNeedsQuery } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { ListDownloadItems, listSelection } from "../../common/download/ListDownloadItems";
import { exportRequest, type ExportCell, type ExportColumn } from "../../common/download/listExport";
import { useApiError } from "../../common/errors/useApiError";
import { displaySum } from "../../common/formatting/displaySum";
import { useNavAccess } from "../../common/layout/useNavAccess";
import { ListLoadError } from "../../common/ListLoadError";
import { displayPersonName } from "../../common/persons/displayPersonName";
import { useProcurementTerms } from "../../common/terminology/useProcurementTerms";
import { PlanBoard } from "./PlanBoard";
import { PlanEntryCard, planValueLabel } from "./PlanEntryCard";
import { IncompletePeriod, OverviewPeriodChoice, useOverviewPeriod } from "./OverviewPeriodChoice";
import {
  filterPlan,
  groupByMonth,
  monthHeading,
  needEntry,
  PLAN_PERIOD_SET,
  planTotals,
  procurementEntry,
  procurementPlanFileName,
  type PlanEntry,
  type PlanMonth,
} from "./procurementPlanEntries";

const PLANNED_NEED_STATUSES = [NeedStatus.Approved, NeedStatus.Planned].join(",");

export const ProcurementPlan = () => {
  const { t } = useTranslation();
  const { message } = useApiError();
  const organization = useLoggedInWithOrgContextState().selectedOrg;
  const { access } = useNavAccess();
  const terms = useProcurementTerms();
  const periodState = useOverviewPeriod(PLAN_PERIOD_SET);
  const filters = usePlanFilters();
  const range = periodState.isUnbounded ? null : periodState.range;
  const procurements = useGetProcurementPlanQuery(
    periodState.isUnbounded || range
      ? {
          orgId: organization.id,
          from: range?.from,
          to: range?.to,
          department: filters.departmentId ?? undefined,
          responsible: filters.responsibleId ?? undefined,
        }
      : skipToken,
  );
  const needs = useListNeedsQuery(access.canAssessNeeds ? { status: PLANNED_NEED_STATUSES } : skipToken);

  const entries = useMemo(
    () => [
      ...(procurements.data?.procurements ?? []).map((planned) => procurementEntry(planned, t, terms.publishedStatus)),
      ...(needs.data?.needs ?? []).map((need) => needEntry(need, t)),
    ],
    [procurements.data, needs.data, t, terms.publishedStatus],
  );
  const departments = organization.departments
    .map((department) => ({ id: department.id, name: department.name }))
    .sort(byName);
  const responsibles = options([
    ...(procurements.data?.responsibles ?? []).map((person) => ({
      id: person.id,
      name: displayPersonName(person).trim(),
    })),
    ...entries.flatMap((entry) => (entry.responsible ? [entry.responsible] : [])),
  ]);
  const isFiltered = !periodState.isUnbounded || filters.departmentId !== null || filters.responsibleId !== null;
  const filterLabels = [
    periodState.isUnbounded ? null : PLAN_PERIOD_SET.label(t, periodState.period),
    departments.find((department) => department.id === filters.departmentId)?.name,
    responsibles.find((person) => person.id === filters.responsibleId)?.name,
  ].filter((label): label is string => Boolean(label));

  const isLoading = procurements.isLoading || needs.isLoading;
  const error = procurements.error ?? needs.error;
  const retry = () => {
    if (procurements.error) void procurements.refetch();
    if (needs.error) void needs.refetch();
  };

  return (
    <Stack spacing="5">
      <Stack spacing="3">
        <OverviewPeriodChoice state={periodState} />
        <Flex gap="2" wrap="wrap">
          <FilterSelect
            label={t("Department")}
            allLabel={t("All departments")}
            options={departments}
            value={filters.departmentId}
            onChange={(value) => filters.set("department", value)}
          />
          <FilterSelect
            label={t("Responsible")}
            allLabel={t("All responsible")}
            options={responsibles}
            value={filters.responsibleId}
            onChange={(value) => filters.set("responsible", value)}
          />
        </Flex>
      </Stack>
      {!periodState.isUnbounded && !range ? (
        <IncompletePeriod />
      ) : isLoading ? (
        <Spinner color="smPrimary" aria-label={String(t("Loading the procurement plan"))} />
      ) : error ? (
        <ListLoadError
          message={message(error)}
          onRetry={retry}
          isRetrying={procurements.isFetching || needs.isFetching}
        />
      ) : (
        <Plan
          entries={filterPlan(entries, {
            range,
            departmentId: filters.departmentId,
            responsibleId: filters.responsibleId,
          })}
          procurementsLeftOut={(procurements.data?.total ?? 0) - (procurements.data?.procurements.length ?? 0)}
          isFiltered={isFiltered}
          onResetFilters={filters.clear}
          filterLabels={filterLabels}
        />
      )}
    </Stack>
  );
};

const usePlanFilters = () => {
  const [params, setParams] = useSearchParams();
  const update = (mutate: (next: URLSearchParams) => void) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  return {
    departmentId: params.get("department"),
    responsibleId: params.get("responsible"),
    set: (key: "department" | "responsible", value: string | null) =>
      update((next) => (value ? next.set(key, value) : next.delete(key))),
    clear: () =>
      update((next) => ["period", "from", "to", "department", "responsible"].forEach((key) => next.delete(key))),
  };
};

type Option = { id: string; name: string };

const FilterSelect = ({
  label,
  allLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  allLabel: string;
  options: Option[];
  value: string | null;
  onChange: (value: string | null) => void;
}) => (
  <Select
    size="sm"
    maxW="64"
    aria-label={label}
    value={value ?? ""}
    onChange={(event) => onChange(event.target.value || null)}
  >
    <option value="">{allLabel}</option>
    {options.map((option) => (
      <option key={option.id} value={option.id}>
        {option.name}
      </option>
    ))}
  </Select>
);

const byName = (a: Option, b: Option) => a.name.localeCompare(b.name, "nb");

const options = (all: Option[]): Option[] =>
  [...new Map(all.map((option) => [option.id, option])).values()].sort(byName);

const Plan = ({
  entries,
  procurementsLeftOut,
  isFiltered,
  onResetFilters,
  filterLabels,
}: {
  entries: PlanEntry[];
  procurementsLeftOut: number;
  isFiltered: boolean;
  onResetFilters: () => void;
  filterLabels: string[];
}) => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const view: PlanView = params.get("view") === "board" ? "board" : "timeline";
  const chooseView = (next: PlanView) =>
    setParams(
      (previous) => {
        const updated = new URLSearchParams(previous);
        if (next === "board") updated.set("view", "board");
        else updated.delete("view");
        return updated;
      },
      { replace: true },
    );
  const totals = planTotals(entries);
  if (entries.length === 0) {
    return isFiltered ? (
      <Stack spacing="2" align="flex-start" data-testid="plan-empty">
        <Text fontSize="sm" color="smMuted">
          {t("Nothing in the procurement plan matches the filters.")}
        </Text>
        <Button size="sm" variant="outline" onClick={onResetFilters}>
          {t("Reset filters")}
        </Button>
      </Stack>
    ) : (
      <Text fontSize="sm" color="smMuted" data-testid="plan-empty">
        {t("No planned or active procurements, and no approved needs waiting to become one.")}
      </Text>
    );
  }
  return (
    <Stack spacing="6">
      <Flex justify="space-between" align="flex-start" gap="3" wrap="wrap">
        <Stack spacing="1" data-testid="plan-total">
          <Text fontWeight="semibold" color="smText">
            {t("{{count}} entries · {{sum}}", {
              count: entries.length,
              sum: displaySum({ value: totals.sum, currency: "NOK" }),
            })}
          </Text>
          {totals.withoutValue > 0 && (
            <Text fontSize="sm" color="smMuted" data-testid="plan-without-value">
              {t("{{count}} entries have no value («Don't know» or «–») and are not included in the sum.", {
                count: totals.withoutValue,
              })}
            </Text>
          )}
          {totals.otherCurrency > 0 && (
            <Text fontSize="sm" color="smMuted" data-testid="plan-other-currency">
              {t("{{count}} entries are valued in another currency and are not included in the sum.", {
                count: totals.otherCurrency,
              })}
            </Text>
          )}
          {procurementsLeftOut > 0 && (
            <Text fontSize="sm" color="smWarning" data-testid="plan-left-out">
              {t(
                "{{count}} more procurements match but are not shown, those with the latest tender deadlines. Narrow the plan with the filters.",
                { count: procurementsLeftOut },
              )}
            </Text>
          )}
        </Stack>
        <Flex gap="2" align="center" wrap="wrap">
          <ButtonGroup size="sm" isAttached variant="outline" role="group" aria-label={String(t("How the plan is shown"))}>
            {(["timeline", "board"] as const).map((option) => (
              <Button
                key={option}
                aria-pressed={view === option}
                variant={view === option ? "solid" : "outline"}
                colorScheme={view === option ? "teal" : undefined}
                onClick={() => chooseView(option)}
              >
                {option === "timeline" ? t("Timeline") : t("Board")}
              </Button>
            ))}
          </ButtonGroup>
          <PlanDownload entries={entries} filterLabels={filterLabels} />
        </Flex>
      </Flex>
      {view === "board" ? (
        <PlanBoard entries={entries} />
      ) : (
        groupByMonth(entries).map((month) => <MonthSection key={month.month ?? "no-date"} month={month} />)
      )}
    </Stack>
  );
};

type PlanView = "timeline" | "board";

const MonthSection = ({ month }: { month: PlanMonth }) => {
  const { t, i18n } = useTranslation();
  return (
    <Stack spacing="3" as="section" aria-label={monthHeading(month.month, t, i18n.language)} data-testid="plan-month">
      <Flex align="baseline" gap="3" wrap="wrap" borderBottomWidth="1px" borderColor="smBorder" pb="1">
        <Heading size="sm" color="smText">
          {monthHeading(month.month, t, i18n.language)}
        </Heading>
        <Text fontSize="sm" color="smMuted">
          {t("{{count}} entries · {{sum}}", {
            count: month.entries.length,
            sum: displaySum({ value: month.sum, currency: "NOK" }),
          })}
        </Text>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="3">
        {month.entries.map((entry) => (
          <PlanEntryCard key={entry.key} entry={entry} />
        ))}
      </SimpleGrid>
    </Stack>
  );
};

const PLAN_COLUMNS = (t: TFunction): ExportColumn[] => [
  { key: "month", label: t("Month") },
  { key: "kind", label: t("Type") },
  { key: "title", label: t("Title") },
  { key: "phase", label: t("Phase") },
  { key: "regulation", label: t("Regulation") },
  { key: "procedure", label: t("Procedure") },
  { key: "responsible", label: t("Responsible") },
  { key: "department", label: t("Department") },
  { key: "value", label: t("Value") },
  { key: "currency", label: t("Currency") },
  { key: "valueSource", label: t("Value used") },
  { key: "deadline", label: t("Tender deadline") },
  { key: "desiredDelivery", label: t("Desired delivery") },
];

const planCell = (key: string, entry: PlanEntry, t: TFunction, language: string): ExportCell => {
  const text = (value: string | null | undefined): ExportCell => (value ? { text: value } : null);
  switch (key) {
    case "month":
      return text(monthHeading(entry.month, t, language));
    case "kind":
      return text(entry.kind === "need" ? t("Need") : t("Procurement"));
    case "title":
      return entry.kind === "procurement" ? { text: entry.title, eventId: entry.id } : text(entry.title);
    case "phase":
      return text(entry.phase);
    case "regulation":
      return text(entry.regulation);
    case "procedure":
      return text(entry.procedure);
    case "responsible":
      return text(entry.responsible?.name);
    case "department":
      return text(entry.department?.name);
    case "value":
      return entry.value !== null ? { number: entry.value } : entry.valueUnknown ? text(t("Don't know")) : null;
    case "currency":
      return entry.value !== null ? text(entry.currency) : null;
    case "valueSource":
      return entry.valueSource ? text(planValueLabel(t, entry)) : null;
    case "deadline":
      return entry.day && entry.deadline ? { date: entry.day } : null;
    case "desiredDelivery":
      return entry.desiredDelivery ? { date: entry.desiredDelivery } : null;
    default:
      return null;
  }
};

const PlanDownload = ({ entries, filterLabels }: { entries: PlanEntry[]; filterLabels: string[] }) => {
  const { t, i18n } = useTranslation();
  const organizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const sorted = groupByMonth(entries).flatMap((month) => month.entries);
  const buildBody = async () =>
    exportRequest(
      PLAN_COLUMNS(t),
      sorted,
      (key, entry) => planCell(key, entry, t, i18n.language),
      listSelection(t, sorted.length, filterLabels),
    );
  return (
    <Menu isLazy closeOnSelect={false} placement="bottom-end">
      {({ onClose }) => (
        <>
          <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FiChevronDown />}>
            {t("Download")}
          </MenuButton>
          <MenuList>
            <ListDownloadItems
              label={t("Procurement plan")}
              endpoint={`/api/buyer/${organizationId}/events/plan/list-export`}
              baseFileName={procurementPlanFileName(new Date())}
              errorMessage={String(t("The procurement plan could not be downloaded. Try again."))}
              buildBody={buildBody}
              isLoading={false}
              isDisabled={entries.length === 0}
              onDone={onClose}
            />
          </MenuList>
        </>
      )}
    </Menu>
  );
};
